import {useEffect, useState} from "react";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Checkbox} from "primereact/checkbox";
import {Dropdown} from "primereact/dropdown";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {Button} from "primereact/button";
import {useDispatch} from "react-redux";
import {copyToClipboard, mi} from "../../system/utils";
import {setMessage} from "../../system/app-slice";
import Icon from "../../system/components/icon";
import {useTranslate} from "../../system/useTranslate";

function relationKey(value) {
    return value?.documentId ?? value?.id ?? null;
}

function PostCategoryDialog({visible, setVisible, collection, category = null, order = 0, onSaved}) {
    const translate = useTranslate();
    const dispatch = useDispatch();

    const [searchText, setSearchText] = useState("");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [excerpt, setExcerpt] = useState("");
    const [isImportant, setIsImportant] = useState(false);
    const [published, setPublished] = useState(false);
    const [parentId, setParentId] = useState(null);
    const [icon, setIcon] = useState("category");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!visible) return;
        setTitle(category?.title ?? "");
        setIcon(category?.icon ?? "category");
        setDescription(category?.description ?? "");
        setExcerpt(category?.excerpt ?? "");
        setIsImportant(Boolean(category?.isImportant));
        setPublished(category?.publicationStatus === "published");
        setParentId(category?.parent ? (category.parent.documentId ?? category.parent.id) : null);
        setSearchText("");
    }, [visible, category]);

    const notify = (severity, key) => dispatch(setMessage({severity, content: translate(key)}));

    const parentOptions = [
        {label: translate("no-parent-post-category"), value: null},
        ...(collection?.items || [])
            .filter((item) => !(category && relationKey(item) === relationKey(category)))
            .map((item) => ({label: item.title, value: relationKey(item)})),
    ];

    const submit = async () => {
        if (!title.trim() || !icon) {
            notify("error", "post-category-items-error");
            return;
        }
        const payload = {
            title: title.trim(),
            icon,
            description,
            excerpt,
            isImportant: Boolean(isImportant),
            publicationStatus: published ? "published" : "draft",
            parent: parentId,
        };
        setSaving(true);
        try {
            if (category?.documentId) {
                await collection.update(category.documentId, payload);
            } else {
                await collection.create({...payload, order});
            }
            onSaved?.();
            setVisible(false);
        } catch (error) {
            notify("error", category?.documentId ? "post-category-update-failed" : "post-category-create-failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            contentClassName="ltr"
            header={translate("create-post-category")}
            className="w-5 h-fit scrollbar-hide rtl"
            visible={visible}
            onHide={() => {
                if (!saving) setVisible(false);
            }}
        >
            <div className="h-full flex flex-column">
                <div className="p-2 flex align-items-end gap-2 rtl">
                    <span className="border-round border-1 border-300 flex align-items-center justify-content-center p-1">
                        <Icon icon={`mi mi:${icon}`} className="text-5xl"/>
                    </span>
                    <div className="flex flex-column flex-1">
                        <label className="text-xs">{translate("post-category-title")}</label>
                        <InputText
                            className="w-full"
                            placeholder={translate("post-category-title-placeholder")}
                            value={title}
                            onChange={(event) => setTitle(event.target.value)}
                        />
                    </div>
                    <div className="flex flex-column flex-1">
                        <label className="text-xs">{translate("parent-post-category")}</label>
                        <Dropdown
                            className="w-full text-xs"
                            options={parentOptions}
                            optionLabel="label"
                            optionValue="value"
                            value={parentId}
                            onChange={(event) => setParentId(event.value)}
                        />
                    </div>
                    <Button
                        className="text-2xs flex justify-content-center p-button-sm p-3 pr-3 pl-4"
                        loading={saving}
                        disabled={saving}
                        label={translate(category?.documentId ? "edit" : "create")}
                        onClick={submit}
                    />
                </div>
                <div className="flex align-items-center justify-content-between w-full rtl p-2 gap-3">
                    <div className="flex align-items-center justify-content-start gap-1">
                        <Checkbox checked={isImportant} onChange={(event) => setIsImportant(event.checked)}/>
                        <label>{translate("show-in-help-index")}</label>
                    </div>
                    <div className="flex align-items-center justify-content-start gap-1">
                        <Checkbox checked={published} onChange={(event) => setPublished(event.checked)} />
                        <label>{translate("published")}</label>
                    </div>
                </div>
                <div className="p-2 flex align-items-center gap-2 rtl">
                    <InputTextarea
                        className="w-full text-xs"
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                    />
                </div>
                <div className="flex flex-column flex-1 rtl p-2">
                    <label className="text-xs">{translate("post-category-excerpt")}</label>
                    <InputText
                        className="w-full text-xs"
                        placeholder={translate("post-category-excerpt-placeholder")}
                        value={excerpt}
                        onChange={(event) => setExcerpt(event.target.value)}
                    />
                </div>
                <div className="p-2">
                    <IconField dir="rtl">
                        <InputIcon className="pi pi-search"/>
                        <InputText
                            className="w-full"
                            placeholder={translate("search-icons-placeholder")}
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                        />
                    </IconField>
                </div>
                <div className="w-full flex flex-wrap gap-3 justify-content-center overflow-auto mt-2 h-30rem">
                    {mi.filter((item) => item.includes(searchText)).map((item) => (
                        <div
                            key={item}
                            onClick={() => {
                                copyToClipboard(`mi mi:${item}`);
                                setIcon(item);
                            }}
                            className="cursor-pointer h-fit flex flex-column align-items-center"
                            title={`mi mi:${item}`}
                        >
                            <Icon icon={`mi mi:${item}`} className="text-2xl"/>
                        </div>
                    ))}
                </div>
            </div>
        </Dialog>
    );
}

export default PostCategoryDialog;